import { flat } from "@suse-o11y/flat";
import { isArrayEqual } from "@suse-o11y/is-array-equal";
import { isDeepEqual } from "@suse-o11y/is-deep-equal";
import { isError } from "@suse-o11y/is-error";
import { isPresent } from "@suse-o11y/is-present";
import { isShallowEqual } from "@suse-o11y/is-shallow-equal";
import { pick } from "@suse-o11y/pick";
import { prop } from "@suse-o11y/prop";
import { type Idle, RemoteData } from "@suse-o11y/remote-data";
import { useMemo } from "@suse-o11y/use-memo";

import type { MetricQuery } from "~/models/metric-query";

import type { TimeRange } from "../../app/state/view/timeline/TimeRange";
import { calculateQueryRangeStep, timeRangeToStep } from "../../metrics/utils";
import { type RangePromQLRequest, type RangePromQLResult, queryRangePromQL } from "../../queries/metrics/batch";
import { type UseFullQueryOptions, type UseQueryOptions, useQueries } from "../../queries/react-query";
import type { TimeSeries } from "../TimeSeriesChart/time-series";

interface UseQueryTimeSeriesParams
	extends Pick<UseQueryOptions, "namespace" | "retry">,
		Pick<RangePromQLRequest, "batch"> {
	timeRange: TimeRange;
	queries: ReadonlyArray<MetricQuery>;

	/**
	 * When defined, the step is derived from the chart width via
	 * {@link calculateQueryRangeStep} so the number of points roughly matches the
	 * pixels available. Use for time-series and bar charts.
	 *
	 * When undefined, the step is derived from the time range only.
	 * Use for gauge and stat charts where values are reduced to a single
	 * number per series and the chart width is irrelevant.
	 */
	width: undefined | number;
}

function useQueryTimeSeries({
	batch,
	namespace,
	retry,
	timeRange,
	queries,
	width,
}: UseQueryTimeSeriesParams): Idle | RemoteData<Error, { isFetching: boolean; timeSeries: Array<TimeSeries> }> {
	const isIdle = queries.length === 0;
	const defaultStep =
		width == null ? timeRangeToStep(timeRange) : calculateQueryRangeStep({ timeRange, chartWidth: width });

	const queryConfigs = useMemo(
		() =>
			queries
				.map((metricQuery) => {
					const query = metricQuery.promql.trim();
					const step = metricQuery.step || defaultStep;

					if (query.length === 0 || !step) {
						return null;
					}

					return {
						namespace,
						retry,
						keepPreviousData: true,
						refetchOnMount: false,
						refetchOnWindowFocus: false,
						refetchOnReconnect: false,

						select: (result: RangePromQLResult) =>
							result.transform((matrixes) =>
								matrixes.map<TimeSeries>((matrix) => ({
									...matrix,
									alias: metricQuery.label,
									componentIdentifier: metricQuery.componentIdentifier,
								})),
							),

						...queryRangePromQL({ batch, query, step, timeRange }),
					} satisfies UseFullQueryOptions;
				})
				.filter(isPresent),
		[queries, namespace, batch, retry, timeRange, defaultStep],
		isDeepEqual,
	);

	const results = useQueries(queryConfigs).map(pick(["data", "error", "isFetching"]));

	return useMemo(
		() => {
			if (isIdle) {
				return RemoteData.idle();
			}

			const timeSeries = results.map((query) => {
				if (isError(query.error)) {
					return RemoteData.failure(query.error);
				}

				if (query.error != null) {
					return RemoteData.failure(new Error("Unknown error"));
				}

				if (query.data == null) {
					return RemoteData.loading();
				}

				return query.data.transformFailure((message) => new Error(message));
			});

			return RemoteData.all({
				isFetching: RemoteData.success(results.some(prop("isFetching"))),
				timeSeries: RemoteData.all(timeSeries).transform(flat()),
			});
		},
		// biome-ignore lint/correctness/useExhaustiveDependencies: as const breaks the rule
		[results, isIdle] as const,
		[(left, right) => isArrayEqual(left, right, isShallowEqual)],
	);
}

export type { UseQueryTimeSeriesParams };
export { useQueryTimeSeries };
